import React, { useEffect, useState, useRef } from "react";
import { api, STATUS_OPTIONS, TASK_TYPES } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import StatusToggle from "./StatusToggle";
import { ExternalLink, Send, MessageSquare, Trash2, X, Pencil, AtSign } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const TYPE_LABEL = TASK_TYPES.reduce((a, t) => { a[t.value] = t.label; return a; }, {});

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString();
}

export default function TaskDetailModal({ task, open, onClose, onUpdated, onDeleted, users, clients }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]); // user_ids
  const [mentionQuery, setMentionQuery] = useState(null); // null when not active
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const endRef = useRef(null);
  const isAdmin = user?.role === "admin";

  const canEdit = ["admin", "editor", "smm"].includes(user?.role);
  const canDelete = ["admin", "smm"].includes(user?.role);

  const findUser = (id) => users.find((u) => u.user_id === id);
  const findClient = (id) => clients.find((c) => c.client_id === id);

  const loadComments = async () => {
    if (!task) return;
    try {
      const r = await api.get(`/tasks/${task.task_id}/comments`);
      setComments(r.data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (_) {}
  };

  useEffect(() => {
    if (open && task) loadComments();
  }, [open, task?.task_id]);

  const onChangeText = (val) => {
    setText(val);
    const at = val.lastIndexOf("@");
    if (at >= 0 && (at === 0 || /\s/.test(val[at - 1]))) {
      const q = val.slice(at + 1);
      if (!q.includes(" ")) {
        setMentionQuery(q);
        return;
      }
    }
    setMentionQuery(null);
  };

  const pickMention = (u) => {
    // Replace the last "@..." with display name and store user_id in mentions
    const at = text.lastIndexOf("@");
    const before = text.slice(0, at);
    const after = `@${u.name} `;
    setText(before + after);
    setMentions((prev) => Array.from(new Set([...prev, u.user_id])));
    setMentionQuery(null);
  };

  const send = async () => {
    const m = text.trim();
    if (!m) return;
    setLoading(true);
    try {
      await api.post(`/tasks/${task.task_id}/comments`, { message: m, mentions });
      setText("");
      setMentions([]);
      await loadComments();
    } catch (e) {
      toast.error("Failed to send");
    } finally {
      setLoading(false);
    }
  };

  const saveEdit = async (c) => {
    const m = editText.trim();
    if (!m) return;
    try {
      await api.patch(`/tasks/${task.task_id}/comments/${c.comment_id}`, { message: m, mentions: c.mentions || [] });
      setEditingId(null);
      setEditText("");
      await loadComments();
    } catch (e) {
      toast.error("Failed to edit");
    }
  };

  const removeComment = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/tasks/${task.task_id}/comments/${c.comment_id}`);
      await loadComments();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const updateStatus = async (status) => {
    try {
      const r = await api.patch(`/tasks/${task.task_id}`, { status });
      onUpdated?.(r.data);
    } catch (e) {
      toast.error("Failed");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await api.delete(`/tasks/${task.task_id}`);
      onDeleted?.(task);
      onClose();
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (!task) return null;
  const editor = findUser(task.assigned_editor_id);
  const smm = findUser(task.assigned_smm_id);
  const cli = findClient(task.client_id);
  const typeLabel = TYPE_LABEL[task.task_type] || task.task_type;

  // Mentionable users for picker
  const mentionables = (users || []).filter((u) =>
    ["admin", "editor", "smm"].includes(u.role) &&
    u.user_id !== user?.user_id &&
    (!mentionQuery || u.name?.toLowerCase().includes((mentionQuery || "").toLowerCase()))
  ).slice(0, 6);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-950 border border-neutral-800 rounded-none max-w-3xl max-h-[90vh] overflow-hidden p-0" data-testid="task-detail-modal">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-neutral-900">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                {typeLabel} {cli ? `· ${cli.name}` : ""}
              </div>
              <DialogTitle className="font-heading text-2xl leading-tight">{task.title}</DialogTitle>
            </div>
            {canDelete && (
              <button onClick={remove} className="text-neutral-600 hover:text-red-400 transition-colors" data-testid="modal-delete-task">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[1fr,320px] divide-y md:divide-y-0 md:divide-x divide-neutral-900 max-h-[70vh]">
          {/* Left: details */}
          <div className="p-6 space-y-5 overflow-y-auto">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Status</div>
              <StatusToggle value={task.status} onChange={updateStatus} disabled={!canEdit} />
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Due date" value={task.due_date || "—"} />
              <Field label="Reminder" value={task.reminder_at ? new Date(task.reminder_at).toLocaleString() : "—"} />
              <Field label="Editor" value={editor?.name || "—"} />
              <Field label="SMM" value={smm?.name || "—"} />
            </div>
            {task.notes && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Notes</div>
                <div className="text-sm text-neutral-200 whitespace-pre-wrap">{task.notes}</div>
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              {task.ref_video_link && (
                <a href={task.ref_video_link} target="_blank" rel="noreferrer" className="text-xs text-neutral-400 hover:text-[#00C97A] inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Reference
                </a>
              )}
              {task.published_url && (
                <a href={task.published_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Published
                </a>
              )}
            </div>
            {task.custom_fields && Object.keys(task.custom_fields).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Custom fields</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(task.custom_fields).map(([k, v]) => (
                    <div key={k} className="border border-neutral-900 px-3 py-2">
                      <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">{k}</div>
                      <div className="text-neutral-200 mt-0.5">{String(v)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: comments */}
          <div className="flex flex-col h-[60vh] md:h-auto">
            <div className="px-4 py-3 border-b border-neutral-900 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <MessageSquare className="w-3 h-3" /> Conversation
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="comments-list">
              {comments.length === 0 && (
                <div className="text-xs text-neutral-600 text-center py-8">
                  Start the thread — nudge an editor, leave a note 💬
                </div>
              )}
              {comments.map((c) => {
                const mine = c.user_id === user?.user_id;
                const canEditComment = mine || isAdmin;
                const isEditing = editingId === c.comment_id;
                return (
                  <div key={c.comment_id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`comment-${c.comment_id}`}>
                    <div className={`max-w-[85%] px-3 py-2 text-sm group relative ${mine ? "bg-[#00C97A]/15 border border-[#00C97A]/30 text-white" : "bg-neutral-900 border border-neutral-800 text-neutral-200"}`}>
                      {!mine && <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-0.5">{c.user_name}</div>}
                      {isEditing ? (
                        <div className="flex gap-1 items-center">
                          <Input
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(c); } if (e.key === "Escape") { setEditingId(null); } }}
                            autoFocus
                            data-testid={`comment-edit-input-${c.comment_id}`}
                            className="rounded-none bg-neutral-950 border-neutral-800 h-7 text-sm min-w-[200px]"
                          />
                          <button onClick={() => saveEdit(c)} data-testid={`comment-edit-save-${c.comment_id}`} className="text-[#00C97A] hover:text-emerald-300"><Send className="w-3 h-3" /></button>
                          <button onClick={() => setEditingId(null)} className="text-neutral-500 hover:text-white"><X className="w-3 h-3" /></button>
                        </div>
                      ) : (
                        <div className="whitespace-pre-wrap">{c.message}</div>
                      )}
                      {c.edited && !isEditing && <span className="text-[9px] text-neutral-500 ml-1">(edited)</span>}
                      {canEditComment && !isEditing && (
                        <div className="absolute -bottom-5 right-0 hidden group-hover:flex gap-1 text-[10px]">
                          {mine && (
                            <button onClick={() => { setEditingId(c.comment_id); setEditText(c.message); }} data-testid={`comment-edit-${c.comment_id}`} className="text-neutral-500 hover:text-[#00C97A]">
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                          <button onClick={() => removeComment(c)} data-testid={`comment-delete-${c.comment_id}`} className="text-neutral-500 hover:text-red-400">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] text-neutral-600 mt-1 font-mono">{timeAgo(c.created_at)}</div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            {mentionQuery !== null && mentionables.length > 0 && (
              <div className="border-t border-neutral-900 max-h-32 overflow-y-auto bg-neutral-900" data-testid="mention-picker">
                {mentionables.map((u) => (
                  <button
                    key={u.user_id}
                    onClick={() => pickMention(u)}
                    data-testid={`mention-${u.user_id}`}
                    className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-neutral-800 transition-colors"
                  >
                    <AtSign className="w-3 h-3 text-[#00C97A]" />
                    <span className="font-medium">{u.name}</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 ml-auto">{u.role}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="px-3 py-3 border-t border-neutral-900 flex gap-2">
              <Input
                value={text}
                onChange={(e) => onChangeText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && mentionQuery === null) { e.preventDefault(); send(); } }}
                placeholder="Type a message… (@ to mention)"
                data-testid="comment-input"
                className="rounded-none bg-neutral-900 border-neutral-800 text-sm"
              />
              <Button onClick={send} disabled={loading || !text.trim()} data-testid="comment-send" className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black h-9 px-3">
                <Send className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</div>
      <div className="text-neutral-200 mt-1">{value}</div>
    </div>
  );
}
